import { Target } from 'lucide-react'

const getBarColor = (pct) => {
  if (pct >= 100) return '#16a34a'
  if (pct >= 80) return '#2563eb'
  if (pct >= 60) return '#d97706'
  return '#dc2626'
}

const ProgressBar = ({ label, pct }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-gray-500 w-16 shrink-0 font-medium">{label}</span>
    <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-500"
        style={{
          width: `${Math.min(pct, 100)}%`,
          backgroundColor: getBarColor(pct)
        }}
      />
    </div>
    <span
      className="text-sm font-bold w-16 text-right shrink-0"
      style={{ color: getBarColor(pct) }}
    >
      {pct.toFixed(1)}%
    </span>
  </div>
)

const LoadingSkeleton = () => (
  <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-4 animate-pulse">
    <div className="flex items-center gap-2 mb-3">
      <div className="h-5 w-5 bg-gray-200 rounded" />
      <div className="h-4 w-48 bg-gray-200 rounded" />
    </div>
    <div className="space-y-2 mb-3">
      <div className="flex items-center gap-2">
        <div className="w-16 h-3 bg-gray-200 rounded" />
        <div className="flex-1 h-3 bg-gray-200 rounded-full" />
        <div className="w-16 h-3 bg-gray-200 rounded" />
      </div>
      <div className="flex items-center gap-2">
        <div className="w-16 h-3 bg-gray-200 rounded" />
        <div className="flex-1 h-3 bg-gray-200 rounded-full" />
        <div className="w-16 h-3 bg-gray-200 rounded" />
      </div>
    </div>
    <div className="flex gap-6">
      <div className="h-3 w-28 bg-gray-200 rounded" />
      <div className="h-3 w-28 bg-gray-200 rounded" />
      <div className="h-3 w-28 bg-gray-200 rounded" />
    </div>
  </div>
)

const TotalGoalsSummary = ({ operators, loadingGoals }) => {
  // Only operators with goal data
  const withGoals = operators.filter(op => {
    const g = op.goalData
    return g && g.forecasted_sales != null && g.goal_value
  })

  // Show skeleton while loading OR when operators exist but goals haven't arrived yet
  if (loadingGoals || (operators.length > 0 && withGoals.length === 0)) {
    return <LoadingSkeleton />
  }

  const totals = withGoals.reduce((acc, op) => {
    const g = op.goalData
    acc.actual += g.actual_sales_mtd || 0
    acc.forecasted += g.forecasted_sales || 0
    acc.goalMin += g.goal_value || 0
    acc.goalTarget += g.goal_value_target || g.goal_value || 0
    return acc
  }, { actual: 0, forecasted: 0, goalMin: 0, goalTarget: 0 })

  const pctMin = totals.goalMin > 0 ? (totals.forecasted / totals.goalMin) * 100 : 0
  const pctTarget = totals.goalTarget > 0 ? (totals.forecasted / totals.goalTarget) * 100 : 0

  return (
    <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm p-4">
      <div className="flex items-center gap-2 mb-3">
        <Target className="h-5 w-5 text-brand-blue" />
        <h3 className="text-sm font-semibold text-brand-gray-800">
          Łączne cele — wszyscy operatorzy
        </h3>
      </div>

      <div className="space-y-2 mb-3">
        <ProgressBar label="Minimum" pct={pctMin} />
        <ProgressBar label="Target" pct={pctTarget} />
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600">
        <span>Prognoza: <strong className="text-gray-900">{Math.round(totals.forecasted)} szt.</strong></span>
        <span>Cel MIN: <strong className="text-gray-900">{Math.round(totals.goalMin)} szt.</strong></span>
        <span>Cel TARGET: <strong className="text-gray-900">{Math.round(totals.goalTarget)} szt.</strong></span>
        <span>Realizacja MTD: <strong className="text-gray-900">{Math.round(totals.actual)} szt.</strong></span>
      </div>
    </div>
  )
}

export default TotalGoalsSummary
